/**
 * SettingsMenu — the gear-button dropdown shared by the matter-page
 * TopBar and the dashboard Home header. Identical content in both
 * contexts: an Active Models summary at the top (Tree / Draft / Fast /
 * Vision / QC / Provenance + PageIndex status) and a list of admin
 * links (Settings, Prompts, Skill Studio, Diagnostic, Users).
 *
 * Every admin link is gated TWICE against non-admin (drafter,
 * reviewer) users:
 *   1. In App.js, the matching ``onOpen*`` prop is undefined
 *      unless isAdmin — drafter users receive no handler.
 *   2. Here in SettingsMenu, the ``currentUserRole`` prop is
 *      checked and any admin entry is dropped unless
 *      currentUserRole === 'admin' — belt-and-braces so a future
 *      caller that forgets the App.js gate doesn't leak admin
 *      surfaces to drafters.
 * Non-admin users see the gear → Active Models section → no links.
 * Server-side require_admin enforces the same contract on the
 * underlying REST endpoints.
 *
 * Lives under ``components/layout/`` because it's header-chrome, used
 * wherever the app renders a top strip. ``matterId`` is optional — on
 * the dashboard it's null and the PageIndex roll-up simply shows the
 * global ON/OFF state; on the matter page it polls that matter's
 * document status every 10 s while the dropdown is open.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Settings, FileCode, Activity, Users, Sparkles, Loader2,
} from 'lucide-react';
import * as api from '@/api/client';


function ActiveModelsSection({
  matterId, open,
}: {
  matterId: string | null;
  open: boolean;
}) {
  const [models, setModels] = useState<api.ActiveModels | null>(null);
  const [loading, setLoading] = useState(false);
  const [pageIndexStatus, setPageIndexStatus] = useState<{
    total: number; indexed: number; failed: number; inflight: number;
  } | null>(null);
  const [retrying, setRetrying] = useState(false);

  // Legacy localStorage cleanup — the old always-on strip used these
  // keys. Safe to run on every mount; noop once wiped.
  useEffect(() => {
    try {
      localStorage.removeItem('topbar.modelsStripCollapsed');
      localStorage.removeItem('topbar.modelsStripCollapsed.v2');
    } catch {}
  }, []);

  // Fetch the active-models config each time the parent dropdown opens.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const m = await api.getActiveModels();
        if (!cancelled) setModels(m);
      } catch {
        if (!cancelled) setModels(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  // Matter-aware PageIndex status. Polls every 10 s while open.
  // Skipped when matterId is null (dashboard context).
  useEffect(() => {
    if (!open || !matterId) { setPageIndexStatus(null); return; }
    let cancelled = false;
    const load = async () => {
      try {
        const data = await api.listMatterDocuments(matterId);
        if (cancelled) return;
        const docs = (data.documents || []).filter(d => d.status !== 'archived');
        const indexed = docs.filter(d => d.status === 'indexed').length;
        const failed = docs.filter(d => d.status === 'failed').length;
        const inflight = docs.filter(d => d.status !== 'indexed' && d.status !== 'failed').length;
        setPageIndexStatus({ total: docs.length, indexed, failed, inflight });
      } catch { /* silent */ }
    };
    load();
    const iv = setInterval(load, 10_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, [open, matterId]);

  const shortName = (full: string): string => {
    if (!full) return '—';
    const after = full.includes('/') ? full.split('/').slice(1).join('/') : full;
    return after.split(':')[0];
  };

  const pageIndexLabel = (() => {
    if (!models) return null;
    if (!models.pageindex_enabled) return { tone: 'text-red-700', text: 'OFF' };
    const s = pageIndexStatus;
    if (!s || s.total === 0) return { tone: 'text-green-700', text: 'ON' };
    if (s.failed > 0) return { tone: 'text-red-700', text: `ON · ${s.indexed}/${s.total} · ${s.failed} failed ⚠` };
    if (s.inflight > 0) return { tone: 'text-amber-700', text: `ON · ${s.indexed}/${s.total} · ${s.inflight} in flight ⏳` };
    return { tone: 'text-green-700', text: `ON · ${s.indexed}/${s.total} ✓` };
  })();

  return (
    <div data-testid="settings-menu-models-section">
      <div className="px-3 pt-2 pb-1 text-[9px] font-mono uppercase tracking-widest text-zinc-500">
        Active Models
      </div>
      {loading && !models && (
        <div className="px-3 py-2 text-[11px] text-zinc-500 inline-flex items-center gap-1">
          <Loader2 size={10} className="animate-spin" /> Loading…
        </div>
      )}
      {!loading && !models && (
        <div className="px-3 py-2 text-[11px] text-zinc-500">
          Unavailable
        </div>
      )}
      {models && (
        <>
          <dl className="px-3 pb-2 space-y-1">
            {([
              { label: 'Tree',       full: models.tree_model },
              { label: 'Draft',      full: models.draft_model },
              { label: 'Fast',       full: models.fast_model },
              { label: 'Vision',     full: models.vision_model },
              { label: 'QC',         full: models.qc_model },
              { label: 'Provenance', full: models.provenance_model },
            ] as Array<{ label: string; full: string }>).map((it) => (
              <div key={it.label} className="flex items-baseline justify-between gap-3 text-[11px]">
                <dt className="text-zinc-500 shrink-0">{it.label}</dt>
                <dd
                  className="font-mono text-zinc-800 truncate"
                  title={it.full || 'not set'}
                >
                  {shortName(it.full)}
                </dd>
              </div>
            ))}
          </dl>
          <div className="px-3 pb-2 flex items-baseline justify-between gap-3 text-[11px]">
            <span className="text-zinc-500">PageIndex</span>
            <span className={`font-mono ${pageIndexLabel?.tone || ''}`}>
              {pageIndexLabel?.text}
            </span>
          </div>
          {matterId && pageIndexStatus && pageIndexStatus.failed > 0 && (
            <div className="px-3 pb-2">
              <button
                type="button"
                data-testid="settings-menu-retry-failed"
                disabled={retrying}
                onClick={async () => {
                  setRetrying(true);
                  try {
                    const data = await api.listMatterDocuments(matterId);
                    const failedDocs = (data.documents || []).filter(d => d.status === 'failed');
                    for (const d of failedDocs) {
                      try { await api.retryMatterDocument(matterId, d.id); } catch {}
                    }
                  } finally { setRetrying(false); }
                }}
                className="w-full text-[10px] text-red-700 border border-red-200 bg-red-50 hover:bg-red-100 rounded-sm py-1 disabled:opacity-50"
              >
                {retrying ? 'Retrying…' : 'Retry failed uploads'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}


export default function SettingsMenu({
  matterId,
  currentUserRole,
  onOpenSettings, onOpenPrompts, onOpenSkill, onOpenDiagnostic, onOpenStageJobs, onOpenUsers,
}: {
  matterId: string | null;
  /** Role of the logged-in user. Admin entries (Settings, Prompts,
   *  Skill Studio, Diagnostic, Stage jobs, Users) render only when
   *  this is 'admin'. Belt-and-braces against a future caller that
   *  forgets to gate the onOpen* props in its parent: if the role
   *  is known to be non-admin we drop every admin entry here
   *  regardless of what was passed in. Null / unset is treated as
   *  non-admin so a bug that leaks the prop never accidentally
   *  exposes admin surfaces. */
  currentUserRole?: string | null;
  onOpenSettings?: () => void;
  onOpenPrompts?: () => void;
  onOpenSkill?: () => void;
  onOpenDiagnostic?: () => void;
  onOpenStageJobs?: () => void;
  onOpenUsers?: () => void;
}) {
  // Role filter: only 'admin' sees the admin entries. Drafter /
  // reviewer / unset role all filter out. This is a secondary
  // gate — primary gating happens in App.js where the handlers
  // themselves are only defined for admins.
  const isAdmin = currentUserRole === 'admin';
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Admin-only entries appear only when their onOpen* prop is passed.
  // Non-admin users see the Active Models section and nothing else.
  const entries: Array<{
    key: string;
    label: string;
    icon: React.ReactNode;
    onClick: () => void;
    testId: string;
  }> = [];
  // Every entry below is gated on isAdmin AND the prop being
  // supplied. Double gate: a caller that forgets isAdmin still
  // has the role filter here; a future admin-sub-role that
  // shouldn't see (say) the Users page can simply pass undefined
  // for that onOpen* prop without touching this file.
  if (isAdmin && onOpenSettings) entries.push({
    key: 'settings', label: 'Settings', icon: <Settings size={14} />,
    onClick: onOpenSettings, testId: 'settings-menu-item-settings',
  });
  if (isAdmin && onOpenPrompts) entries.push({
    key: 'prompts', label: 'Prompts', icon: <FileCode size={14} />,
    onClick: onOpenPrompts, testId: 'settings-menu-item-prompts',
  });
  if (isAdmin && onOpenSkill) entries.push({
    key: 'skill', label: 'Skill Studio', icon: <Sparkles size={14} />,
    onClick: onOpenSkill, testId: 'settings-menu-item-skill',
  });
  if (isAdmin && onOpenDiagnostic) entries.push({
    key: 'diagnostic', label: 'Diagnostic', icon: <Activity size={14} />,
    onClick: onOpenDiagnostic, testId: 'settings-menu-item-diagnostic',
  });
  if (isAdmin && onOpenStageJobs) entries.push({
    key: 'stage_jobs', label: 'Stage jobs', icon: <Activity size={14} />,
    onClick: onOpenStageJobs, testId: 'settings-menu-item-stage-jobs',
  });
  if (isAdmin && onOpenUsers) entries.push({
    key: 'users', label: 'Users', icon: <Users size={14} />,
    onClick: onOpenUsers, testId: 'settings-menu-item-users',
  });

  return (
    <div ref={menuRef} className="relative inline-block">
      <button
        type="button"
        data-testid="settings-menu-toggle"
        onClick={() => setOpen((v) => !v)}
        title="Settings"
        aria-expanded={open}
        aria-haspopup="menu"
        className="text-zinc-500 hover:text-zinc-900 transition-colors p-1.5 inline-flex items-center gap-1 text-xs"
      >
        <Settings size={16} />
      </button>
      {open && (
        <div
          data-testid="settings-menu"
          role="menu"
          className="absolute right-0 top-full mt-1 z-[60] w-[260px] rounded-sm border border-zinc-200 bg-white shadow-xl py-1 text-xs"
        >
          <ActiveModelsSection matterId={matterId} open={open} />
          {entries.length > 0 && (
            <>
              <div className="my-1 border-t border-zinc-100" />
              {entries.map((e) => (
                <button
                  key={e.key}
                  type="button"
                  role="menuitem"
                  data-testid={e.testId}
                  onClick={() => { setOpen(false); e.onClick(); }}
                  className="w-full flex items-center gap-2 px-3 py-2 text-left text-zinc-700 hover:bg-zinc-50 hover:text-zinc-900"
                >
                  <span className="text-zinc-500">{e.icon}</span>
                  {e.label}
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
